import React, { useEffect } from 'react';
import ChecklistPanelCollapsed from './ChecklistPanelCollapsed';
import ChecklistPanelExpanded from './ChecklistPanelExpanded';
import { useChecklistStore } from '@/stores/checklistStore';
import { useOnboardingChecklist } from '@/hooks/useOnboardingChecklist';

interface ChecklistPanelProps {
  entrepriseId: string;
}

/**
 * Panneau Checklist "Premiers pas" - Orchestration de l'état ouvert/fermé
 * Affiche les étapes d'onboarding pour guider les nouveaux utilisateurs
 */
const ChecklistPanel = ({ entrepriseId }: ChecklistPanelProps) => {
  const { isOpen, openPanel, closePanel } = useChecklistStore();
  const {
    items,
    isVisible,
    loading,
    completedCount,
    totalCount,
    progressPercent,
    isAllCompleted,
    showAsGuide,
    hideChecklist,
    hideCompletely,
    resetChecklist,
  } = useOnboardingChecklist(entrepriseId);

  // Ouvrir automatiquement le panneau tant que la checklist n'est pas complétée
  // ET que l'utilisateur n'a pas explicitement masqué la checklist (showAsGuide = mode Guide forcé)
  useEffect(() => {
    // Attendre que le chargement soit terminé et qu'on ait des items
    if (loading || totalCount === 0 || !isVisible) return;

    // Ouvrir automatiquement si pas complété ET pas déjà en mode Guide forcé
    if (!isAllCompleted && !showAsGuide) {
      openPanel();
    }
  }, [loading, totalCount, isVisible, isAllCompleted, showAsGuide, openPanel]);

  // Ne pas afficher si masqué définitivement ou pas d'items
  if (!isVisible || totalCount === 0) {
    return null;
  }

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════
          ONGLET PLIÉ - Visible quand fermé
          ═══════════════════════════════════════════════════════════════ */}
      <ChecklistPanelCollapsed
        isVisible={!isOpen}
        onExpand={openPanel}
        completedCount={completedCount}
        totalCount={totalCount}
        isAllCompleted={isAllCompleted}
        showAsGuide={showAsGuide}
      />

      {/* ═══════════════════════════════════════════════════════════════
          OVERLAY - Fond sombre quand ouvert
          ═══════════════════════════════════════════════════════════════ */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={closePanel}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════
          PANNEAU DÉPLIÉ - Visible quand ouvert
          ═══════════════════════════════════════════════════════════════ */}
      <ChecklistPanelExpanded
        isVisible={isOpen}
        onCollapse={closePanel}
        items={items}
        completedCount={completedCount}
        totalCount={totalCount}
        progressPercent={progressPercent}
        isAllCompleted={isAllCompleted || showAsGuide}
        onHideChecklist={hideChecklist}
        onHideCompletely={hideCompletely}
        onResetChecklist={resetChecklist}
      />
    </>
  );
};

export default ChecklistPanel;
